import { useEffect, useState, useCallback, createContext, useContext } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, FolderOpen, Files, BookMarked, Upload, CreditCard, UserRound, Loader2, MoreHorizontal } from "lucide-react";
import { webApi } from "@/lib/webApi";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Eyebrow } from "@/pages/web/ui";
import { Overview, Matters, MatterDetail } from "@/pages/web/WebMatters";
import { FilesView } from "@/pages/web/WebFiles";
import { UploadView } from "@/pages/web/WebUpload";
import { Billing, Account } from "@/pages/web/WebAccount";

const MeCtx = createContext(null);
export const useMe = () => useContext(MeCtx);

const NAV = [
  ["/app", "Overview", LayoutGrid, true, "Workspace"], ["/app/matters", "Matters", FolderOpen, undefined, "Workspace"], ["/app/files", "Files", Files, undefined, "Workspace"],
  ["/app/library", "Library", BookMarked, undefined, "Workspace"], ["/app/upload", "Upload", Upload, undefined, "Practice"], ["/app/billing", "Plan", CreditCard, undefined, "Account"],
  ["/app/account", "Account", UserRound, undefined, "Account"],
];
const GROUPS = ["Workspace", "Practice", "Account"];
const BAR = ["Overview", "Matters", "Files", "Upload"];

function Nav({ me }) {
  const [more, setMore] = useState(false);
  const { pathname } = useLocation();
  const cls = ({ isActive }) =>
    `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${isActive ? "bg-stone-100 font-semibold text-[--w-text]" : "text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]"}`;
  const mob = ({ isActive }) => `flex flex-1 flex-col items-center gap-1 py-2 text-[11px] ${isActive ? "font-semibold text-[--w-text]" : "text-[--w-text-3]"}`;
  const bar = NAV.filter(([, label]) => BAR.includes(label));
  const rest = NAV.filter(([, label]) => !BAR.includes(label));
  const moreActive = rest.some(([to]) => pathname === to || pathname.startsWith(`${to}/`));
  return (
    <>
      <aside className="fixed inset-y-0 left-0 hidden w-60 border-r border-[--w-border] bg-[--w-card] px-3 py-5 md:flex md:flex-col" data-testid="web-sidebar">
        <div className="px-3 font-display text-2xl font-bold text-[--w-text]">Dicta</div>
        <nav>
          {GROUPS.map((g) => (
            <div key={g}>
              <Eyebrow className="mb-1 mt-5 px-3">{g}</Eyebrow>
              <div className="space-y-1">
                {NAV.filter((n) => n[4] === g).map(([to, label, Icon, end]) => (
                  <NavLink key={to} to={to} end={end} className={cls} data-testid={`web-nav-${label.toLowerCase()}`}><Icon className="h-4 w-4" /> {label}</NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <Link to="/app/account" className="mt-auto flex items-center gap-3 rounded-md px-3 py-2 transition-colors duration-200 hover:bg-[--w-card-2]">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[--w-text] text-sm font-semibold text-white">{(me.name || "D").trim().charAt(0).toUpperCase() || "D"}</span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{me.name}</span>
            <span className="block text-xs text-[--w-text-3]">{me.plan_label}</span>
          </span>
        </Link>
      </aside>
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-[--w-border] bg-[--w-card] md:hidden" data-testid="web-bottom-bar">
        {bar.map(([to, label, Icon, end]) => (
          <NavLink key={to} to={to} end={end} className={mob} data-testid={`web-mnav-${label.toLowerCase()}`}><Icon className="h-4 w-4" />{label}</NavLink>
        ))}
        <button type="button" aria-label="More" onClick={() => setMore(true)} className={mob({ isActive: moreActive })}><MoreHorizontal className="h-4 w-4" />More</button>
      </nav>
      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="bottom" className="theme-warm border-[--w-border] bg-[--w-card] pb-8">
          <SheetTitle className="sr-only">More</SheetTitle>
          <div className="space-y-1 pt-2">
            {rest.map(([to, label, Icon, end]) => (
              <NavLink key={to} to={to} end={end} onClick={() => setMore(false)} className={cls} data-testid={`web-mnav-${label.toLowerCase()}`}><Icon className="h-4 w-4" /> {label}</NavLink>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default function WebApp() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const load = useCallback(() => webApi.get("/me").then((r) => setMe(r.data)).catch(() => nav("/app/login", { replace: true })), [nav]);

  useEffect(() => {
    load();
  }, [load]);

  if (!me) return <div className="theme-warm flex min-h-screen items-center justify-center bg-[--w-bg]"><Loader2 className="h-5 w-5 animate-spin text-[--w-text-3]" /></div>;
  return (
    <MeCtx.Provider value={{ me, reload: load }}>
      <div className="theme-warm min-h-screen bg-[--w-bg] text-[--w-text] tabular-nums" data-testid="web-app">
        <Nav me={me} />
        <main className="px-4 pb-24 pt-4 sm:px-8 md:ml-60 md:pb-10">
          <div className="mx-auto max-w-6xl">
            <Routes>
              <Route index element={<Overview />} />
              <Route path="matters" element={<Matters />} />
              <Route path="matters/:id" element={<MatterDetail />} />
              <Route path="files" element={<FilesView />} />
              <Route path="library" element={<FilesView library />} />
              <Route path="upload" element={<UploadView />} />
              <Route path="billing" element={<Billing />} />
              <Route path="account" element={<Account />} />
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </MeCtx.Provider>
  );
}

export function PageHead({ title, sub, children }) {
  return (
    <div className="mb-6 flex min-h-16 flex-wrap items-center justify-between gap-3 border-b border-[--w-border] pb-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-[--w-text] sm:text-2xl" data-testid="web-page-title">{title}</h1>
        {sub && <p className="mt-0.5 text-sm text-[--w-text-2]">{sub}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="rounded-[--w-radius] border border-dashed border-[--w-border-2] bg-[--w-card] p-8 text-center text-sm text-[--w-text-2]" data-testid="web-empty">{children}</div>;
}

export const btn = "inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200";
export const primary = `${btn} bg-[--w-brand] text-white hover:bg-stone-800`;
export const ghost = `${btn} border border-[--w-border] bg-[--w-card] text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]`;
